// src/encounter/intelligence/acknowledgement.tsx
//
// The AI notice gate in front of the Scrips Intelligence cards (DoH §3.4 AI-literacy acknowledgement). Contract read
// from scrips-clinician-demo at 13df5c0, scripts/foundry/scripsServer.ts `acknowledgement()` and
// src/engine/acknowledgements.ts:
//   GET  /api/intelligence/acknowledgement -> 200 { version 'ai-literacy/3', text, lines[], confirm, listed, acknowledged: {version, date, name} | null }
//   POST /api/intelligence/acknowledgement { confirmed: true, name, version } -> 201 { acknowledged }
//        400 bad name / body, 403 not listed, 409 note version changed, 503 store down.
// Rules: the notice lines and the confirm label are rendered verbatim (the app writes no notice text); the cards show
// only once `acknowledged` is set for the current version; a person not on the named list sees nothing; a malformed
// reply fails closed (no cards). Same auth as the Explain card (the clinician's own token). Nothing logs. The name is
// typed by the person, never pre-filled.
import { INTELLIGENCE_BASE } from '../intelligence-engine';

export const ACK_PATH = `${INTELLIGENCE_BASE}/acknowledgement`;

export type FetchLike = (input: string, init: RequestInit) => Promise<Response>;

export interface AckNotice {
  version: string;
  lines: string[];
  confirm: string;
  listed: boolean;
  acknowledged: { version: string; date: string; name: string } | null;
}

/** Fixed, non-clinical lines of the gate. */
export const ACK_LINE = {
  name: 'Type your full name (first and last).',
  changed: 'The notice has changed. Read it again.',
  notRecorded: 'Not recorded. Try again.',
} as const;

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown, max = 2000): v is string => typeof v === 'string' && v.length > 0 && v.length <= max;

/** The `acknowledged` field: the ack, null for none, undefined when malformed. */
export function parseAck(v: unknown): AckNotice['acknowledged'] | undefined {
  if (v === null) return null;
  if (!isObj(v) || !str(v.version, 64) || !str(v.date, 32) || !str(v.name, 200)) return undefined;
  return { version: v.version, date: v.date, name: v.name };
}

/** A2: the GET reply checked on arrival; null when malformed (the gate then fails closed). */
export function parseAckNotice(raw: unknown): AckNotice | null {
  if (!isObj(raw) || !str(raw.version, 64) || !str(raw.confirm, 500) || typeof raw.listed !== 'boolean') return null;
  if (!Array.isArray(raw.lines) || raw.lines.length === 0 || raw.lines.length > 30 || !raw.lines.every((l) => str(l))) return null;
  const acknowledged = parseAck(raw.acknowledged);
  if (acknowledged === undefined) return null;
  return { version: raw.version, lines: raw.lines as string[], confirm: raw.confirm, listed: raw.listed, acknowledged };
}

/** Mirrors the engine's name rule loosely (two words or more); the engine still decides. */
export const looksLikeFullName = (n: string) => n.trim().split(/\s+/).filter(Boolean).length >= 2;

